import React, { Component } from 'react';
import {
  Stack,
  IconButton,
  Typography,
  Box,
  Menu,
  MenuItem,
  Collapse,
  ButtonBase,
} from '@mui/material';
import { faChevronDown, faEllipsisVertical } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import WidgetMenu from './WidgetMenu';
import { connect } from 'react-redux';
import { savePreferences } from '../SetUp/actions';
import { deepClone } from '@mui/x-data-grid/utils/utils';
import { deepCloneObject } from './utils';
import { toggleWidgetSelection } from '../SetUp/utils';
import { VOICEOVER_ACTIVATION_GUARD_MS, VOICEOVER_REFOCUS_DELAY_MS } from './constants';

class Widget extends Component {
  static defaultProps = {
    recentToggles: new Map(),
  };

  state = {};
  headerRef = React.createRef();

  componentDidMount() {}

  //θ(N) wher N is the number of widgets
  //Updates the isExpand value of the widget and triggers a throttle to save preferences via api.
  updatePreferences = () => {
    let preferences = deepClone(this.props.preferences);

    let widgetsOrder = preferences?.Widgets?.WidgetsOrder;

    //Loop over widgets order to find the widget whose isExpand status needs to be changed
    widgetsOrder = widgetsOrder?.map((section) => {
      return section.map((widget) => {
        if (widget.id === this.props.data.id) {
          widget.isExpand = !widget.isExpand;
        }
        return widget;
      });
    });

    preferences.Widgets.WidgetsOrder = widgetsOrder;

    //Trigger the throttle
    this.props.triggerPreferenceThrottle();

    //Save Preferences in redux
    this.props.savePreferences(preferences);

    this.props.updateWidgets(preferences);
  };

  // VoiceOver on iOS/macOS fires the click handler twice in quick succession
  // when a user double-taps or activates a button — once from the VO gesture
  // and once from the synthetic click it dispatches. Without a guard, the
  // widget expands then immediately collapses/collapses.
  // VOICEOVER_ACTIVATION_GUARD_MS is the time window to ignore 2nd click
  setWidgetExpanded = (isExpanded) => {
    const id = this.props.data.id;
    if (this.props.recentToggles.has(id)) return;
    this.props.recentToggles.set(id, true);
    setTimeout(() => this.props.recentToggles.delete(id), VOICEOVER_ACTIVATION_GUARD_MS);

    let preferences = deepClone(this.props.preferences);
    let widgetsOrder = preferences?.Widgets?.WidgetsOrder;

    widgetsOrder = widgetsOrder?.map((section) => {
      return section.map((widget) => {
        if (widget.id === this.props.data.id && widget.isExpand !== isExpanded) {
          widget.isExpand = isExpanded;
        }
        return widget;
      });
    });

    preferences.Widgets.WidgetsOrder = widgetsOrder;
    this.props.triggerPreferenceThrottle();
    this.props.savePreferences(preferences);
    this.props.updateWidgets(preferences);

    // Re-anchor VoiceOver's virtual cursor after React commits the DOM update
    setTimeout(() => {
      if (this.headerRef.current) {
        this.headerRef.current.focus();
      }
    }, VOICEOVER_REFOCUS_DELAY_MS);
  };

  onHideWidget = (widgetId) => {
    let preferences = deepClone(this.props.preferences);

    const widgetsOrder = toggleWidgetSelection(preferences.Widgets.WidgetsOrder, widgetId);
    preferences.Widgets.WidgetsOrder = widgetsOrder;

    //Save in redux
    this.props.savePreferences(preferences);
    this.props.updateWidgets(preferences);
    this.props.triggerPreferenceThrottle();
  };

  writeTitle = () => {
    if (this.props.data.isTitleStylized) {
      return (
        <Stack direction="row" className="myOdu__stylizedText">
          {this.props?.data?.title?.map((word, index) => (
            <Typography
              component="span"
              id={this.props.data.id + '_text_titleWord' + index}
              key={this.props.data.id + '_title_word_' + index}
            >
              {word}
            </Typography>
          ))}
        </Stack>
      );
    } else return this.props.data.title;
  };

  isAccordionExpanded = () => {
    return this.props.data.isExpand;
  };

  getTitleText = () => {
    const { title } = this.props.data;
    return Array.isArray(title) ? title.join('') : title;
  };

  render() {
    const titleText = this.getTitleText();
    const expanded = this.isAccordionExpanded();

    return (
      <Box
        className={'myOdu__widget ' + (this.props.className || '')}
        id={this.props.data.id + '__accordion'}
        sx={{ position: 'relative' }}
      >
        {/* //for accessibility, the mui button doesnt play nice with VO + iOS */}
        <ButtonBase
          ref={this.headerRef}
          component="button"
          onClick={() => this.setWidgetExpanded(!expanded)}
          id={this.props.data.id + '__accordionSummary'}
          aria-expanded={expanded}
          aria-controls={this.props.data.id + '__accordionDetails'}
          aria-label={titleText + ' widget'}
          className={'header' + (expanded ? ' Mui-expanded' : '')}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            cursor: 'pointer',
            zIndex: 100,
            width: '100%',
            justifyContent: 'flex-start',
            textAlign: 'left',
          }}
          disableRipple
        >
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1)',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              marginLeft: '0.5rem',
            }}
          >
            <FontAwesomeIcon
              icon={faChevronDown}
              fixedWidth
              id={this.props.data.id + '__chevron_accordionSummary'}
            />
          </span>
          {this.props.data.logo ? (
            <img src={this.props.data.logo} className="myOdu__widgetLogo" alt={this.writeTitle()} />
          ) : (
            <Typography
              id={this.props.data.id + '__title'}
              component="h2"
              className="myOdu__widgetTitle"
            >
              {this.writeTitle()}
            </Typography>
          )}
          <span id={this.props.data.id + '__box'} style={{ flexGrow: 1 }} />
        </ButtonBase>
        {(this.props.data.menu || !this.props.data.isRequired) && (
          <Box
            sx={{ position: 'absolute', top: '0', right: '0.5rem', zIndex: 101 }}
            id={this.props.data.id + '__widgetMenu_wrapper'}
          >
            <WidgetMenu
              data={{
                title: this.props.data.title,
                id: this.props.data.id,
                isMenuDisabled: this.props.data.isMenuDisabled,
                isRequired: this.props.data.isRequired,
              }}
              menu={this.props.data.menu}
              onHideWidget={this.onHideWidget}
            />
          </Box>
        )}
        <Collapse in={expanded}>
          <Box
            id={this.props.data.id + '__accordionDetails'}
            role="region"
            aria-labelledby={this.props.data.id + '__accordionSummary'}
            className={
              'myOdu__widgetDetails ' +
              (this.props.hasScrollY ? ' scrollY' : '') +
              (this.props.hasScrollX ? ' scrollX' : '')
            }
          >
            {this.props.children}
          </Box>
        </Collapse>
      </Box>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    preferences: state.preferencesReducer.preferences,
    user: state.AWSReducer.user,
    isImpersonating: state.impersonationReducer.impersonation?.isImpersonating ?? false,
    impersonation: state.impersonationReducer.impersonation,
  };
};

const mapDispatchToProps = (dispatch) => ({
  savePreferences: (preferences) => dispatch(savePreferences(preferences)),
});
export default connect(mapStateToProps, mapDispatchToProps)(Widget);
